import {
    Alert,
    AlertIcon,
    Badge,
    Box,
    Button,
    Flex,
    HStack,
    Icon,
    IconButton,
    Image,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Spinner,
    Switch,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    Tooltip,
    useDisclosure,
    useToast,
    VStack,
} from '@chakra-ui/react';
import {
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { keyframes } from '@emotion/react';
import imageCompression from 'browser-image-compression';
import {
    TbCheck,
    TbCopy,
    TbDotsVertical,
    TbDownload,
    TbEye,
} from 'react-icons/tb';
import SessionContext from '../../contexts/SessionContext';
import {
    fetchAllInventoryImportPreviewRows,
    useCancelInventoryImportJob,
    useCommitInventoryImportJob,
    useInventoryImportJobsList,
    useInventoryImportPreview,
    useUpdateInventoryImportJob,
    waitForInventoryImportJobSettled,
} from '../../hooks/inventory/useInventoryImportJobs';
import { uploadInventoryImage } from '../../hooks/inventory/useUploadInventoryImage';
import type {
    InventoryCategory,
    InventoryImportDraftRow,
    InventoryImportDraftRowChanges,
    InventoryImportJob,
    InventoryImportRowFilter,
    InventoryTag,
    InventoryBin,
    InventoryUnit,
} from '../../types/inventory';
import type { ImportTargetField } from '../../functions/import/fuzzyColumnMatch';
import { INVENTORY_IMPORT_TARGET_FIELDS } from '../../functions/import/inventoryImportFields';
import { formatPrice } from '../../functions';
import { formatPartNumber } from '../../functions/formatPartNumber';
import { formatInventoryImportIssuesFilename } from '../../utils/inventoryCsvFilename';
import {
    formatInventoryPartTypeLabel,
    formatInventoryUnitLabel,
    INVENTORY_CATEGORY_OPTIONS,
    parseInventoryPartType,
} from '../../utils/inventoryConstants';
import { dedupeTagNames, draftInventoryTag } from '../../utils/inventoryTags';
import Pagination from '../Pagination';
import { InventoryImportDefaultValueField } from './InventoryImportDefaultValueField';
import InventoryImageCropDialog from './InventoryImageCropDialog';
import InventoryTagsField from './InventoryTagsField';
import InventoryBinsField from './InventoryBinsField';
import YaxleLogo from '../../assets/yaxleLogoBlue.png';

const PAGE_SIZE = 20;

const logoSpin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

const barSweep = keyframes`
    0% { transform: translateX(-120%); }
    60% { transform: translateX(320%); }
    100% { transform: translateX(320%); }
`;

/**
 * Fills the remaining import-preview pane (below the job list) with a spinning
 * Yaxle mark and an indeterminate brand sweep. Not tied to processedItemCount —
 * the job jumps from 0 to done, so a real percentage would sit at 0 the whole time.
 */
const ImportProcessingCard = ({
    title,
    detail,
}: {
    title: string;
    detail?: string;
}) => (
    <Flex
        flex={1}
        minH={0}
        w="100%"
        mb={4}
        align="center"
        justify="center"
        bg="bg.surface"
        borderWidth="1px"
        borderColor="border.subtle"
        borderRadius="2xl"
        px={{ base: 8, md: 16 }}
        py={{ base: 10, md: 16 }}
    >
        <VStack spacing={{ base: 8, md: 12 }} w="min(42rem, 100%)">
            <Image
                src={YaxleLogo}
                alt=""
                boxSize={{ base: '96px', md: '140px' }}
                draggable={false}
                animation={`${logoSpin} 2.8s linear infinite`}
            />
            <VStack spacing={2}>
                <Text textStyle="h3" color="fg.default" textAlign="center">
                    {title}
                </Text>
                {detail && (
                    <Text textStyle="bodyLarge" color="fg.muted" textAlign="center">
                        {detail}
                    </Text>
                )}
            </VStack>
            <Box
                w="100%"
                h="10px"
                borderRadius="full"
                bg="bg.subtle"
                overflow="hidden"
                position="relative"
            >
                <Box
                    position="absolute"
                    top={0}
                    bottom={0}
                    w="40%"
                    borderRadius="full"
                    bgGradient="linear(to-r, brand.500, accent.400)"
                    animation={`${barSweep} 1.6s ease-in-out infinite`}
                />
            </Box>
            <Text textStyle="body" color="fg.muted" textAlign="center">
                Hang tight — this can take a few minutes.
            </Text>
        </VStack>
    </Flex>
);

const ISSUE_FOCUS_FIELD_ORDER = [
    'name',
    'manufacturerPartNumber',
    'additionalPartNumbers',
    'category',
    'partType',
    'supplierName',
    'initialInStockQuantity',
    'unitCost',
    'locationTitle',
    'unit',
    'restockAlert',
    'origin',
    'reorderQuantity',
    'targetQuantity',
    'binNames',
    'tagNames',
    'imageUrl',
] as const;

/**
 * Click-to-edit columns in visual order, used for Tab / Shift+Tab navigation on
 * any row. Excludes `tagNames` / `binNames` (always-on editors, no discrete edit mode) and
 * `imageUrl` (crop dialog).
 */
const EDITABLE_TAB_FIELD_ORDER = [
    'name',
    'manufacturerPartNumber',
    'additionalPartNumbers',
    'category',
    'partType',
    'supplierName',
    'initialInStockQuantity',
    'unitCost',
    'locationTitle',
    'unit',
    'restockAlert',
    'origin',
    'reorderQuantity',
    'targetQuantity',
] as const;

const FIELD_BY_ID = Object.fromEntries(
    INVENTORY_IMPORT_TARGET_FIELDS.map((f) => [f.id, f]),
) as Record<string, ImportTargetField>;

function jobStatusColor(status: string): string {
    switch (status) {
        case 'draft':
            return 'gray';
        case 'committed':
            return 'blue';
        case 'failed':
            return 'red';
        case 'uploading':
        case 'processing':
        case 'queued':
            return 'orange';
        default:
            return 'gray';
    }
}

function jobStatusLabel(status: string): string {
    switch (status) {
        case 'uploading':
            return 'UPLOADING';
        case 'queued':
        case 'processing':
            return 'PROCESSING';
        case 'draft':
            return 'DRAFT';
        case 'failed':
            return 'FAILED';
        case 'committed':
            return 'COMMITTED';
        default:
            return status.toUpperCase();
    }
}

function isRowEditable(job: InventoryImportJob | undefined): boolean {
    if (!job) return false;
    if (job.status === 'queued' || job.status === 'processing' || job.status === 'failed') {
        return false;
    }
    return job.status === 'draft';
}

function isImportTasksJob(job: InventoryImportJob): boolean {
    return job.status !== 'committed';
}

function mergeDisplayRow(
    row: InventoryImportDraftRow,
    dirty?: InventoryImportDraftRowChanges,
): InventoryImportDraftRow {
    if (!dirty) return row;
    return { ...row, ...dirty };
}

/** True when effective in-stock quantity is 0 (out of stock). */
function isOutOfStock(
    row: Pick<InventoryImportDraftRow, 'initialInStockQuantity'>,
): boolean {
    return (row.initialInStockQuantity ?? 0) === 0;
}

function categoryDisplayLabel(category: string | null | undefined): string {
    if (!category) return '';
    return (
        INVENTORY_CATEGORY_OPTIONS.find((o) => o.value === category)?.label ?? category
    );
}

function csvEscape(value: string): string {
    if (/[",\n\r]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
    return value;
}

const FLAGGED_CSV_COLUMNS: Array<{ key: string; label: string }> = [
    { key: 'name', label: 'Name' },
    { key: 'manufacturerPartNumber', label: 'Mfg. Part Number' },
    { key: 'additionalPartNumbers', label: 'Additional Part Numbers' },
    { key: 'category', label: 'Category' },
    { key: 'partType', label: 'Part Type' },
    { key: 'supplierName', label: 'Supplier' },
    { key: 'locationTitle', label: 'Location' },
    { key: 'unit', label: 'Unit' },
    { key: 'initialInStockQuantity', label: 'In-Stock Quantity' },
    { key: 'unitCost', label: 'Avg Unit Cost' },
    { key: 'restockAlert', label: 'Restock Alert' },
    { key: 'origin', label: 'Origin' },
    { key: 'reorderQuantity', label: 'Reorder Quantity' },
    { key: 'targetQuantity', label: 'Target Quantity' },
    { key: 'binNames', label: 'Bins' },
    { key: 'tagNames', label: 'Tags' },
    { key: 'issues', label: 'Issues' },
];

function downloadFlaggedCsv(rows: InventoryImportDraftRow[]) {
    const lines = [FLAGGED_CSV_COLUMNS.map((c) => csvEscape(c.label)).join(',')];
    rows.forEach((row) => {
        const issues = row.issues.map((i) => `${i.field ?? ''}: ${i.message}`).join('; ');
        lines.push(
            FLAGGED_CSV_COLUMNS.map((col) => {
                if (col.key === 'issues') return csvEscape(issues);
                if (col.key === 'tagNames') return csvEscape((row.tagNames ?? []).join('; '));
                if (col.key === 'binNames') return csvEscape((row.binNames ?? []).join('; '));
                if (col.key === 'additionalPartNumbers') {
                    return csvEscape((row.additionalPartNumbers ?? []).join('; '));
                }
                if (col.key === 'category') return csvEscape(categoryDisplayLabel(row.category));
                if (col.key === 'unitCost') {
                    return csvEscape(row.unitCost != null ? String(row.unitCost) : '');
                }
                const v = row[col.key as keyof InventoryImportDraftRow];
                return csvEscape(v == null ? '' : String(v));
            }).join(','),
        );
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = formatInventoryImportIssuesFilename();
    a.click();
    URL.revokeObjectURL(url);
}

function fieldValuesEqual(
    field: string,
    original: InventoryImportDraftRow,
    nextValue: unknown,
): boolean {
    if (field === 'tagNames' || field === 'binNames' || field === 'additionalPartNumbers') {
        const a = [
            ...((original[field as 'tagNames' | 'binNames' | 'additionalPartNumbers'] ??
                []) as string[]),
        ]
            .map(String)
            .sort()
            .join('\0');
        const b = [...((nextValue as string[] | undefined) ?? [])].map(String).sort().join('\0');
        return a === b;
    }
    const orig = original[field as keyof InventoryImportDraftRow];
    if (orig == null && (nextValue == null || nextValue === '')) return true;
    return String(orig ?? '') === String(nextValue ?? '');
}

function rowHasUndirtyIssueField(
    row: InventoryImportDraftRow,
    dirtyFields?: Set<string>,
): boolean {
    if (row.issues.length === 0) return false;
    return row.issues.some((issue) => {
        if (!issue.field) return true;
        return !dirtyFields?.has(issue.field);
    });
}

function issueMessageForField(
    row: InventoryImportDraftRow,
    field: string,
    dirtyFields?: Set<string>,
): string | null {
    if (dirtyFields?.has(field)) return null;
    const issue = row.issues.find((i) => i.field === field);
    return issue?.message ?? null;
}

function FieldIssueIcon({
    message,
    onClick,
}: {
    message: string | null;
    onClick?: () => void;
}) {
    if (!message) return null;
    return (
        <Tooltip label={message} hasArrow>
            <Box
                as="span"
                display="inline-flex"
                alignItems="center"
                ml={1}
                flexShrink={0}
                alignSelf="center"
                cursor={onClick ? 'pointer' : undefined}
                onClick={(e) => {
                    if (!onClick) return;
                    e.stopPropagation();
                    onClick();
                }}
            >
                <Alert status="error" variant="subtle" p={0} bg="transparent" w="auto">
                    <AlertIcon boxSize={3.5} m={0} mr={0} />
                </Alert>
            </Box>
        </Tooltip>
    );
}

interface InventoryImportPreviewPanelProps {
    selectedJobId: string | null;
    onSelectJobId: (jobId: string) => void;
    optimisticImports?: Array<{
        tempId: string;
        status: 'uploading' | 'failed';
        totalItemCount: number;
        createdAt: string;
        errorMessage?: string;
    }>;
    selectedOptimisticTempId?: string | null;
    onSelectOptimisticTempId?: (tempId: string | null) => void;
}

export function InventoryImportPreviewPanel({
    selectedJobId,
    onSelectJobId,
    optimisticImports = [],
    selectedOptimisticTempId = null,
    onSelectOptimisticTempId,
}: InventoryImportPreviewPanelProps) {
    const toast = useToast();
    const queryClient = useQueryClient();
    const sessionContext = useContext(SessionContext);
    const { data: jobsData, isLoading: jobsLoading, refetch: refetchJobs } =
        useInventoryImportJobsList(true);
    const jobs = useMemo(() => jobsData?.jobs ?? [], [jobsData?.jobs]);

    const [rowFilter, setRowFilter] = useState<InventoryImportRowFilter>('all');
    const [page, setPage] = useState(1);
    const [skippedRowIds, setSkippedRowIds] = useState<Record<string, true>>({});
    /** When on, treat rows with in-stock quantity 0 as skipped (Skip Out-of-Stock). */
    const [skipOutOfStock, setSkipOutOfStock] = useState(false);
    const [dirtyByRowId, setDirtyByRowId] = useState<
        Record<string, InventoryImportDraftRowChanges>
    >({});
    const [dirtyFieldsByRowId, setDirtyFieldsByRowId] = useState<Record<string, Set<string>>>({});
    const [focusChain, setFocusChain] = useState<{
        rowId: string;
        fields: string[];
        index: number;
        /** Issue-resolution chains drop fields as they're fixed; Tab-nav chains keep them. */
        prune: boolean;
    } | null>(null);
    const [imageCrop, setImageCrop] = useState<{
        rowId: string;
        url: string;
        name: string;
        mfg: string;
    } | null>(null);
    const [imageKeysByRowId, setImageKeysByRowId] = useState<Record<string, string>>({});
    const [isImportPreparing, setIsImportPreparing] = useState(false);
    const [knownFlaggedRows, setKnownFlaggedRows] = useState<InventoryImportDraftRow[]>([]);
    const [rowsByIdCache, setRowsByIdCache] = useState<Record<string, InventoryImportDraftRow>>(
        {},
    );
    const [isDownloadingFlagged, setIsDownloadingFlagged] = useState(false);
    const [awaitingValidate, setAwaitingValidate] = useState(false);
    const [awaitingImport, setAwaitingImport] = useState(false);
    const sawImportProcessingRef = useRef(false);
    const imageCropDisclosure = useDisclosure();
    const [copyToastJobId, setCopyToastJobId] = useState<string | null>(null);

    useEffect(() => {
        setPage(1);
    }, [selectedJobId, rowFilter]);

    useEffect(() => {
        setSkippedRowIds({});
        setSkipOutOfStock(false);
        setDirtyByRowId({});
        setDirtyFieldsByRowId({});
        setFocusChain(null);
        setKnownFlaggedRows([]);
        setRowsByIdCache({});
        setImageKeysByRowId({});
        setImageCrop(null);
        setAwaitingValidate(false);
        setAwaitingImport(false);
        sawImportProcessingRef.current = false;
    }, [selectedJobId]);

    const taskJobs = useMemo(() => {
        const server = jobs.filter(isImportTasksJob);
        const combined = [
            ...optimisticImports.map((o) => ({
                kind: 'optimistic' as const,
                tempId: o.tempId,
                status: o.status as string,
                totalItemCount: o.totalItemCount,
                createdAt: o.createdAt,
                errorMessage: o.errorMessage,
                jobId: null as string | null,
                processedItemCount: undefined as number | undefined,
                issueCount: undefined as number | undefined,
            })),
            ...server.map((j) => ({
                kind: 'server' as const,
                tempId: null as string | null,
                status: j.status as string,
                totalItemCount: j.totalItemCount,
                createdAt: j.createdAt,
                errorMessage: j.errorMessage,
                jobId: j.jobId as string | null,
                processedItemCount: j.processedItemCount,
                issueCount: j.issueCount,
            })),
        ].sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        const oldestFirst = [...combined].reverse();
        const numberByKey = new Map<string, number>();
        oldestFirst.forEach((row, idx) => {
            numberByKey.set(row.jobId ?? row.tempId!, idx + 1);
        });
        return combined.map((row) => ({
            ...row,
            jobNumber: numberByKey.get(row.jobId ?? row.tempId!) ?? 0,
        }));
    }, [jobs, optimisticImports]);

    useEffect(() => {
        if (selectedJobId || selectedOptimisticTempId) return;
        if (taskJobs.length === 0) return;
        const first = taskJobs[0];
        if (first.jobId) onSelectJobId(first.jobId);
        else if (first.tempId) onSelectOptimisticTempId?.(first.tempId);
    }, [
        taskJobs,
        selectedJobId,
        selectedOptimisticTempId,
        onSelectJobId,
        onSelectOptimisticTempId,
    ]);

    const selectedJob = jobs.find((j) => j.jobId === selectedJobId);

    const previewQuery = useInventoryImportPreview(
        selectedJobId
            ? {
                  jobId: selectedJobId,
                  limit: PAGE_SIZE,
                  offset: (page - 1) * PAGE_SIZE,
                  rowFilter,
              }
            : null,
        !!selectedJobId &&
            selectedJob?.status === 'draft' &&
            !awaitingValidate &&
            !awaitingImport,
    );

    const rows = useMemo(() => previewQuery.data?.rows ?? [], [previewQuery.data?.rows]);
    const totalItems = previewQuery.data?.totalItems ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

    useEffect(() => {
        if (rows.length === 0) return;
        setRowsByIdCache((prev) => {
            const next = { ...prev };
            rows.forEach((r) => {
                next[r.rowId] = r;
            });
            return next;
        });
    }, [rows]);

    useEffect(() => {
        if (
            !selectedJobId ||
            !sessionContext?.sessionId ||
            !selectedJob ||
            (selectedJob.issueCount ?? 0) === 0 ||
            (selectedJob.status !== 'draft' && selectedJob.status !== 'committed')
        ) {
            return;
        }
        let cancelled = false;
        fetchAllInventoryImportPreviewRows(
            sessionContext.sessionId,
            selectedJobId,
            'flagged',
        ).then((flagged) => {
            if (cancelled) return;
            setKnownFlaggedRows(flagged);
            setRowsByIdCache((prev) => {
                const next = { ...prev };
                flagged.forEach((r) => {
                    next[r.rowId] = r;
                });
                return next;
            });
        });
        return () => {
            cancelled = true;
        };
    }, [
        selectedJobId,
        selectedJob,
        sessionContext?.sessionId,
    ]);

    const hasDirtyEdits = Object.keys(dirtyByRowId).length > 0;

    const markDirty = useCallback(
        (rowId: string, serverRow: InventoryImportDraftRow, changes: InventoryImportDraftRowChanges) => {
            const applied: InventoryImportDraftRowChanges = {};
            const cleared: string[] = [];
            (Object.keys(changes) as Array<keyof InventoryImportDraftRowChanges>).forEach((key) => {
                const value = changes[key];
                if (fieldValuesEqual(key, serverRow, value)) {
                    cleared.push(key);
                } else {
                    (applied as Record<string, unknown>)[key] = value;
                }
            });

            setDirtyByRowId((prev) => {
                const merged = { ...prev[rowId], ...applied };
                cleared.forEach((k) => {
                    delete merged[k as keyof InventoryImportDraftRowChanges];
                });
                const next = { ...prev };
                if (Object.keys(merged).length === 0) delete next[rowId];
                else next[rowId] = merged;
                return next;
            });

            setDirtyFieldsByRowId((prev) => {
                const nextSet = new Set(prev[rowId] ?? []);
                Object.keys(applied).forEach((k) => nextSet.add(k));
                cleared.forEach((k) => nextSet.delete(k));
                const next = { ...prev };
                if (nextSet.size === 0) delete next[rowId];
                else next[rowId] = nextSet;
                return next;
            });

            if (Object.keys(applied).length > 0) {
                setFocusChain((chain) => {
                    if (!chain || chain.rowId !== rowId) return chain;
                    // Tab-nav chains keep the full column order so navigation is stable.
                    if (!chain.prune) return chain;
                    const edited = new Set(Object.keys(applied));
                    const fields = chain.fields.filter((f) => !edited.has(f));
                    if (fields.length === 0) return null;
                    const index = Math.min(chain.index, fields.length - 1);
                    return { ...chain, fields, index };
                });
            }
        },
        [],
    );

    const organizationId = sessionContext?.organizationId ?? '';

    /** Drop cached preview pages so All/Flagged never flash pre-validate rows (global staleTime is 5m). */
    const dropJobPreviewCaches = useCallback(() => {
        if (!selectedJobId) return;
        queryClient.removeQueries({
            queryKey: ['/inventory/import/jobs', organizationId, selectedJobId],
        });
    }, [queryClient, selectedJobId, organizationId]);

    const updateJobMutation = useUpdateInventoryImportJob({
        onSuccess: async (data) => {
            setDirtyByRowId({});
            setDirtyFieldsByRowId({});
            setSkippedRowIds({});
            setSkipOutOfStock(false);
            setImageKeysByRowId({});
            setFocusChain(null);
            setRowsByIdCache({});
            setKnownFlaggedRows([]);
            await refetchJobs();

            const finishValidateSuccess = () => {
                setAwaitingValidate(false);
                dropJobPreviewCaches();
            };

            if (data.job.status === 'processing' || data.job.status === 'queued') {
                try {
                    await waitForInventoryImportJobSettled(
                        sessionContext?.sessionId ?? '',
                        data.job.jobId,
                    );
                    await refetchJobs();
                    finishValidateSuccess();
                } catch (err: unknown) {
                    setAwaitingValidate(false);
                    toast({
                        title: 'Could not validate edits',
                        description:
                            err instanceof Error ? err.message : 'Update failed',
                        status: 'error',
                        duration: 4000,
                        isClosable: true,
                    });
                }
                return;
            }

            if (
                data.job.status === 'draft' ||
                data.job.status === 'committed' ||
                data.job.status === 'failed'
            ) {
                finishValidateSuccess();
            }
        },
        onError: (err: unknown) => {
            setAwaitingValidate(false);
            toast({
                title: 'Could not validate edits',
                description: err instanceof Error ? err.message : 'Update failed',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        },
    });

    const cancelMutation = useCancelInventoryImportJob({
        onSuccess: (_data, variables) => {
            refetchJobs();
            queryClient.invalidateQueries({ queryKey: ['/inventory/import/jobs/list'] });
            if (variables.jobId === selectedJobId) {
                const remaining = jobs.filter((j) => j.jobId !== variables.jobId);
                if (remaining[0]) {
                    onSelectJobId(remaining[0].jobId);
                }
            }
            toast({
                title: 'Import job cancelled',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        },
        onError: (err: unknown) => {
            toast({
                title: 'Cancel failed',
                description: err instanceof Error ? err.message : 'Could not cancel job',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        },
    });

    const commitMutation = useCommitInventoryImportJob({
        onSuccess: async (data) => {
            queryClient.invalidateQueries({ queryKey: ['/inventory/feed'] });
            queryClient.invalidateQueries({ queryKey: ['/inventory/activity-logs'] });
            queryClient.invalidateQueries({ queryKey: ['/inventory/import/jobs/list'] });
            queryClient.invalidateQueries({
                queryKey: ['/inventory/activity-logs', 'inventory-imported'],
            });
            dropJobPreviewCaches();
            const remaining = jobs.filter(
                (j) => j.jobId !== selectedJobId && isImportTasksJob(j),
            );
            if (remaining[0]) {
                onSelectJobId(remaining[0].jobId);
                setAwaitingImport(false);
                sawImportProcessingRef.current = false;
            } else {
                // Keep processing UI until the list drops this job — clearing
                // awaitingImport while list still has stale `draft` flashes the table.
                await refetchJobs();
                setAwaitingImport(false);
                sawImportProcessingRef.current = false;
            }
            toast({
                title: 'Import complete',
                description: `Created ${data.createdCount} item(s).`,
                status: 'success',
                duration: 4000,
                isClosable: true,
            });
        },
        onError: (err: unknown) => {
            setAwaitingImport(false);
            sawImportProcessingRef.current = false;
            toast({
                title: 'Import failed',
                description: err instanceof Error ? err.message : 'Could not import',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        },
    });

    const issueRows = useMemo(() => rows.filter((r) => r.issues.length > 0), [rows]);

    const skippableRows = useMemo(() => {
        const byId = new Map<string, InventoryImportDraftRow>();
        knownFlaggedRows.forEach((r) => byId.set(r.rowId, r));
        issueRows.forEach((r) => byId.set(r.rowId, r));
        Object.values(rowsByIdCache).forEach((r) => {
            if (r.issues.length > 0) byId.set(r.rowId, r);
        });
        return Array.from(byId.values()).filter(
            (r) =>
                !skippedRowIds[r.rowId] &&
                rowHasUndirtyIssueField(r, dirtyFieldsByRowId[r.rowId]),
        );
    }, [knownFlaggedRows, issueRows, rowsByIdCache, skippedRowIds, dirtyFieldsByRowId]);

    const hasSkippableIssueRows = skippableRows.length > 0;

    const handleSkipAllIssues = () => {
        if (skippableRows.length === 0) return;
        const next = { ...skippedRowIds };
        skippableRows.forEach((r) => {
            next[r.rowId] = true;
        });
        setSkippedRowIds(next);
    };

    const handleValidateEdits = () => {
        if (!selectedJobId || !hasDirtyEdits) return;
        const rowsPayload = Object.entries(dirtyByRowId).map(([rowId, changes]) => {
            const nextChanges = { ...changes };
            if (imageKeysByRowId[rowId]) {
                nextChanges.imageUrl = imageKeysByRowId[rowId];
            }
            return { rowId, changes: nextChanges };
        });
        setAwaitingValidate(true);
        updateJobMutation.mutate({ jobId: selectedJobId, rows: rowsPayload });
    };

    const handleImportClick = async () => {
        if (
            !selectedJobId ||
            !sessionContext?.sessionId ||
            hasDirtyEdits ||
            hasSkippableIssueRows
        ) {
            return;
        }
        setIsImportPreparing(true);
        setAwaitingImport(true);
        sawImportProcessingRef.current = false;
        try {
            const all = await fetchAllInventoryImportPreviewRows(
                sessionContext.sessionId,
                selectedJobId,
                'all',
            );
            const toCommit = all.filter((r) => {
                if (skippedRowIds[r.rowId]) return false;
                if (
                    skipOutOfStock &&
                    isOutOfStock(mergeDisplayRow(r, dirtyByRowId[r.rowId]))
                ) {
                    return false;
                }
                return true;
            });
            if (toCommit.length === 0) {
                setAwaitingImport(false);
                toast({
                    title: 'Nothing to import',
                    description: 'All rows are skipped.',
                    status: 'warning',
                    duration: 4000,
                    isClosable: true,
                });
                return;
            }

            commitMutation.mutate({
                jobId: selectedJobId,
                rowIds: toCommit.map((r) => r.rowId),
            });
        } catch (err: unknown) {
            setAwaitingImport(false);
            toast({
                title: 'Import failed',
                description: err instanceof Error ? err.message : 'Could not load rows',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsImportPreparing(false);
        }
    };

    const handleImageCropConfirm = async (file: File) => {
        if (!imageCrop || !sessionContext?.sessionId) return;
        const { rowId } = imageCrop;
        const serverRow = rowsByIdCache[rowId] ?? rows.find((r) => r.rowId === rowId);
        if (!serverRow) return;
        imageCropDisclosure.onClose();
        setImageCrop(null);
        try {
            const compressed = await imageCompression(file, {
                maxSizeMB: 1.5,
                maxWidthOrHeight: 1600,
                useWebWorker: true,
            });
            const asFile = new File([compressed], file.name, {
                type: file.type || 'image/jpeg',
            });
            const key = await uploadInventoryImage(sessionContext.sessionId, asFile);
            const objectUrl = URL.createObjectURL(asFile);
            setImageKeysByRowId((prev) => ({ ...prev, [rowId]: key }));
            markDirty(rowId, serverRow, { imageUrl: objectUrl });
        } catch (err: unknown) {
            toast({
                title: 'Image update failed',
                description: err instanceof Error ? err.message : 'Could not process image',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        }
    };

    const handleDownloadFlagged = async () => {
        if (!selectedJobId || !sessionContext?.sessionId) return;
        setIsDownloadingFlagged(true);
        try {
            const flagged = await fetchAllInventoryImportPreviewRows(
                sessionContext.sessionId,
                selectedJobId,
                'flagged',
            );
            downloadFlaggedCsv(flagged);
        } catch (err: unknown) {
            toast({
                title: 'Download failed',
                description: err instanceof Error ? err.message : 'Could not load flagged rows',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setIsDownloadingFlagged(false);
        }
    };

    const startEditFocusChain = (row: InventoryImportDraftRow) => {
        const dirtyFields = dirtyFieldsByRowId[row.rowId];
        const issueFields = new Set(
            row.issues.map((i) => i.field).filter((f): f is string => !!f),
        );
        const fields = ISSUE_FOCUS_FIELD_ORDER.filter(
            (f) => issueFields.has(f) && !dirtyFields?.has(f),
        );
        if (fields.length === 0) return;
        setFocusChain({ rowId: row.rowId, fields: [...fields], index: 0, prune: true });
    };

    const showTable =
        selectedJob &&
        selectedJob.status === 'draft' &&
        !awaitingValidate &&
        !awaitingImport;

    const showProcessing =
        selectedJob &&
        (awaitingValidate ||
            awaitingImport ||
            selectedJob.status === 'queued' ||
            selectedJob.status === 'processing');

    const copyJobId = async (jobId: string) => {
        try {
            await navigator.clipboard.writeText(jobId);
            setCopyToastJobId(jobId);
            toast({
                title: 'Job ID copied',
                status: 'success',
                duration: 1500,
                isClosable: true,
            });
        } catch {
            toast({
                title: 'Could not copy',
                status: 'error',
                duration: 2000,
                isClosable: true,
            });
        }
    };

    const selectedOptimistic = optimisticImports.find((o) => o.tempId === selectedOptimisticTempId);

    const displayStatusForTask = (row: (typeof taskJobs)[number]): string => {
        if (
            row.jobId &&
            row.jobId === selectedJobId &&
            (awaitingImport || awaitingValidate)
        ) {
            return 'processing';
        }
        return row.status;
    };

    return (
        <Box flex={1} display="flex" flexDirection="column" overflow="hidden" minH={0} px={6}>
            <Box mb={4} flexShrink={0}>
                <Text fontSize="md" fontWeight="bold" color="gray.800" mb={3}>
                    Import Tasks
                </Text>
                {jobsLoading && taskJobs.length === 0 ? (
                    <Spinner size="sm" />
                ) : taskJobs.length === 0 ? (
                    <Text fontSize="sm" color="gray.500">
                        No import jobs yet. Use Inventory Options → Import Items to start.
                    </Text>
                ) : (
                    <VStack align="stretch" spacing={2} maxH="180px" overflowY="auto">
                        {taskJobs.map((row) => {
                            const selected =
                                (row.jobId != null && row.jobId === selectedJobId) ||
                                (row.tempId != null && row.tempId === selectedOptimisticTempId);
                            const status = displayStatusForTask(row);
                            return (
                                <Flex
                                    key={row.jobId ?? row.tempId!}
                                    align="center"
                                    justify="space-between"
                                    px={3}
                                    py={2}
                                    borderRadius="md"
                                    borderWidth="1px"
                                    borderColor={selected ? 'blue.400' : 'gray.200'}
                                    bg={selected ? 'blue.50' : 'white'}
                                    _hover={{ bg: selected ? 'blue.50' : 'gray.50' }}
                                    gap={2}
                                    cursor="pointer"
                                    onClick={() => {
                                        if (row.jobId) onSelectJobId(row.jobId);
                                        else if (row.tempId) onSelectOptimisticTempId?.(row.tempId);
                                    }}
                                >
                                    <HStack
                                        spacing={2}
                                        flex={1}
                                        minW={0}
                                        flexWrap="wrap"
                                        fontSize="sm"
                                        color="gray.800"
                                    >
                                        <Text fontWeight="semibold" flexShrink={0}>
                                            Job {row.jobNumber}
                                        </Text>
                                        <Text color="gray.400" flexShrink={0}>
                                            |
                                        </Text>
                                        {row.jobId ? (
                                            <>
                                                <HStack spacing={1} minW={0} flexShrink={1}>
                                                    <Text
                                                        fontFamily="mono"
                                                        noOfLines={1}
                                                        userSelect="text"
                                                        cursor="text"
                                                        title={row.jobId}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        {row.jobId}
                                                    </Text>
                                                    <IconButton
                                                        aria-label="Copy job ID"
                                                        icon={
                                                            <Icon
                                                                as={
                                                                    (copyToastJobId === row.jobId
                                                                        ? TbCheck
                                                                        : TbCopy) as React.ElementType
                                                                }
                                                            />
                                                        }
                                                        size="xs"
                                                        variant="ghost"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            void copyJobId(row.jobId!);
                                                        }}
                                                    />
                                                </HStack>
                                                <Text color="gray.400" flexShrink={0}>
                                                    |
                                                </Text>
                                            </>
                                        ) : null}
                                        <Badge colorScheme={jobStatusColor(status)}>
                                            {jobStatusLabel(status)}
                                        </Badge>
                                    </HStack>
                                    <HStack spacing={1} flexShrink={0} align="center">
                                        <Text fontSize="sm" color="gray.600" whiteSpace="nowrap">
                                            {row.processedItemCount != null &&
                                            (status === 'processing' || status === 'queued')
                                                ? `${row.processedItemCount}/${row.totalItemCount}`
                                                : `${row.totalItemCount} items`}
                                            {row.issueCount != null && row.issueCount > 0
                                                ? ` · ${row.issueCount} issues`
                                                : ''}
                                        </Text>
                                        {row.jobId ? (
                                            <Menu>
                                                <MenuButton
                                                    as={IconButton}
                                                    aria-label="Job actions"
                                                    icon={
                                                        <Icon
                                                            as={
                                                                TbDotsVertical as React.ElementType
                                                            }
                                                        />
                                                    }
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                                <MenuList minW="140px" py={1}>
                                                    <MenuItem
                                                        color="red.600"
                                                        onClick={() =>
                                                            cancelMutation.mutate({
                                                                jobId: row.jobId!,
                                                            })
                                                        }
                                                        isDisabled={cancelMutation.isPending}
                                                    >
                                                        Cancel
                                                    </MenuItem>
                                                </MenuList>
                                            </Menu>
                                        ) : null}
                                    </HStack>
                                </Flex>
                            );
                        })}
                    </VStack>
                )}
            </Box>

            {showTable ? (
                <Box flex={1} minH={0} display="flex" flexDirection="column">
                    <Flex justify="space-between" align="center" mb={3} flexWrap="wrap" gap={2}>
                        <HStack spacing={1}>
                            {(['all', 'flagged'] as const).map((filter) => (
                                <Button
                                    key={filter}
                                    size="sm"
                                    h="32px"
                                    px={4}
                                    borderRadius="full"
                                    fontWeight="medium"
                                    fontSize="sm"
                                    bg={rowFilter === filter ? '#0071E3' : 'gray.100'}
                                    color={rowFilter === filter ? 'white' : 'gray.700'}
                                    _hover={{
                                        bg: rowFilter === filter ? '#3544a0' : 'gray.200',
                                    }}
                                    onClick={() => setRowFilter(filter)}
                                >
                                    {filter === 'all' ? 'All' : 'Flagged'}
                                </Button>
                            ))}
                        </HStack>
                            <HStack spacing={2} flexWrap="wrap">
                            {rowFilter === 'flagged' ? (
                                <Tooltip label="Download items with issues" hasArrow>
                                    <IconButton
                                        aria-label="Download items with issues"
                                        icon={<Icon as={TbDownload as React.ElementType} />}
                                        size="sm"
                                        variant="ghost"
                                        isLoading={isDownloadingFlagged}
                                        onClick={handleDownloadFlagged}
                                    />
                                </Tooltip>
                            ) : null}
                            <HStack spacing={2} align="center">
                                <Switch
                                    size="sm"
                                    isChecked={skipOutOfStock}
                                    onChange={(e) => setSkipOutOfStock(e.target.checked)}
                                    aria-label="Skip Out-of-Stock"
                                />
                                <Text fontSize="sm" color="gray.700" whiteSpace="nowrap">
                                    Skip Out-of-Stock
                                </Text>
                            </HStack>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={handleSkipAllIssues}
                                isDisabled={!hasSkippableIssueRows}
                            >
                                Skip Items with Issues
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={handleValidateEdits}
                                isDisabled={!hasDirtyEdits}
                                isLoading={updateJobMutation.isPending}
                            >
                                Validate Edits
                            </Button>
                            <Button
                                size="sm"
                                bg="#0071E3"
                                color="white"
                                _hover={{ bg: '#3544a0' }}
                                onClick={handleImportClick}
                                isDisabled={
                                    hasSkippableIssueRows ||
                                    hasDirtyEdits ||
                                    selectedJob.status !== 'draft'
                                }
                                isLoading={
                                    isImportPreparing || commitMutation.isPending
                                }
                            >
                                Import
                            </Button>
                        </HStack>
                    </Flex>

                    {previewQuery.isLoading || (previewQuery.isFetching && rows.length === 0) ? (
                        <Flex justify="center" py={10}>
                            <Spinner />
                        </Flex>
                    ) : previewQuery.isError ? (
                        <Alert status="error">
                            <AlertIcon />
                            Failed to load preview rows.
                        </Alert>
                    ) : (
                        <Box
                            flex={1}
                            overflow="auto"
                            borderWidth="1px"
                            borderColor="gray.200"
                            borderRadius="md"
                        >
                            <Table size="sm" sx={{ 'th:not(:first-of-type), td:not(:first-of-type)': { whiteSpace: 'nowrap' } }}>
                                <Thead position="sticky" top={0} bg="gray.50" zIndex={1}>
                                    <Tr>
                                        <Th>Issue Action</Th>
                                        <Th whiteSpace="nowrap">Name</Th>
                                        <Th whiteSpace="nowrap">Mfg. Part Number</Th>
                                        <Th whiteSpace="nowrap">Additional Part Numbers</Th>
                                        <Th whiteSpace="nowrap" width="1%">
                                            Category
                                        </Th>
                                        <Th whiteSpace="nowrap" width="1%">
                                            Part Type
                                        </Th>
                                        <Th whiteSpace="nowrap" minW="202px">
                                            Supplier
                                        </Th>
                                        <Th isNumeric whiteSpace="nowrap" width="1%">
                                            In-Stock
                                        </Th>
                                        <Th isNumeric whiteSpace="nowrap" width="1%">
                                            Avg Unit Cost
                                        </Th>
                                        <Th whiteSpace="nowrap" minW="168px">
                                            Location
                                        </Th>
                                        <Th whiteSpace="nowrap" width="1%" minW="88px">
                                            Unit
                                        </Th>
                                        <Th whiteSpace="nowrap" width="1%">
                                            Restock Alert
                                        </Th>
                                        <Th whiteSpace="nowrap" width="1%">
                                            Origin
                                        </Th>
                                        <Th isNumeric whiteSpace="nowrap" width="1%">
                                            Reorder
                                        </Th>
                                        <Th isNumeric whiteSpace="nowrap" width="1%">
                                            Target
                                        </Th>
                                        <Th whiteSpace="nowrap">Bins</Th>
                                        <Th whiteSpace="nowrap">Tags</Th>
                                        <Th whiteSpace="nowrap" width="1%">
                                            Image
                                        </Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {rows.map((row) => {
                                        const displayRow = mergeDisplayRow(
                                            row,
                                            dirtyByRowId[row.rowId],
                                        );
                                        return (
                                        <PreviewRow
                                            key={row.rowId}
                                            row={row}
                                            displayRow={displayRow}
                                            editable={isRowEditable(selectedJob)}
                                            skipped={
                                                !!skippedRowIds[row.rowId] ||
                                                (skipOutOfStock && isOutOfStock(displayRow))
                                            }
                                            dirtyFields={dirtyFieldsByRowId[row.rowId]}
                                            focusField={
                                                focusChain?.rowId === row.rowId
                                                    ? focusChain.fields[focusChain.index]
                                                    : null
                                            }
                                            issueFocusActive={
                                                focusChain?.rowId === row.rowId &&
                                                focusChain.prune
                                            }
                                            onToggleSkip={() =>
                                                setSkippedRowIds((prev) => {
                                                    const next = { ...prev };
                                                    if (next[row.rowId]) delete next[row.rowId];
                                                    else next[row.rowId] = true;
                                                    return next;
                                                })
                                            }
                                            onEdit={() => startEditFocusChain(row)}
                                            onDirty={(changes) =>
                                                markDirty(row.rowId, row, changes)
                                            }
                                            onFocusAdvance={(opts) => {
                                                if (opts.exit) {
                                                    setFocusChain(null);
                                                    return;
                                                }
                                                const fromFieldId = opts.fromFieldId;
                                                const step =
                                                    opts.direction === 'backward' ? -1 : 1;
                                                setFocusChain((chain) => {
                                                    // No active chain → start a full-column
                                                    // Tab-nav chain from the current field.
                                                    if (!chain || chain.rowId !== row.rowId) {
                                                        if (!fromFieldId) return chain;
                                                        const base =
                                                            EDITABLE_TAB_FIELD_ORDER.indexOf(
                                                                fromFieldId as never,
                                                            );
                                                        if (base === -1) return chain;
                                                        const target = base + step;
                                                        if (
                                                            target < 0 ||
                                                            target >=
                                                                EDITABLE_TAB_FIELD_ORDER.length
                                                        ) {
                                                            return null;
                                                        }
                                                        return {
                                                            rowId: row.rowId,
                                                            fields: [
                                                                ...EDITABLE_TAB_FIELD_ORDER,
                                                            ],
                                                            index: target,
                                                            prune: false,
                                                        };
                                                    }
                                                    if (fromFieldId) {
                                                        const fromIdx =
                                                            chain.fields.indexOf(fromFieldId);
                                                        // markDirty may have pruned this field
                                                        // (issue chains), leaving index on next.
                                                        if (fromIdx === -1) {
                                                            if (chain.fields.length === 0) {
                                                                return null;
                                                            }
                                                            if (step < 0) {
                                                                const t = chain.index - 1;
                                                                return t < 0
                                                                    ? null
                                                                    : { ...chain, index: t };
                                                            }
                                                            return chain;
                                                        }
                                                        const target = fromIdx + step;
                                                        if (
                                                            target < 0 ||
                                                            target >= chain.fields.length
                                                        ) {
                                                            return null;
                                                        }
                                                        return { ...chain, index: target };
                                                    }
                                                    const target = chain.index + step;
                                                    if (
                                                        target < 0 ||
                                                        target >= chain.fields.length
                                                    ) {
                                                        return null;
                                                    }
                                                    return { ...chain, index: target };
                                                });
                                            }}
                                            onViewImage={(url, name, mfg) => {
                                                setImageCrop({
                                                    rowId: row.rowId,
                                                    url,
                                                    name,
                                                    mfg,
                                                });
                                                imageCropDisclosure.onOpen();
                                            }}
                                        />
                                        );
                                    })}
                                </Tbody>
                            </Table>
                        </Box>
                    )}

                    {totalPages > 1 ? (
                        <Box py={3}>
                            <Pagination
                                currentPage={page}
                                totalPages={totalPages}
                                onPageChange={setPage}
                                hasNextPage={page < totalPages}
                            />
                        </Box>
                    ) : null}
                </Box>
            ) : showProcessing ? (
                <ImportProcessingCard
                    title="Processing your import…"
                    detail={
                        selectedJob?.totalItemCount
                            ? `${selectedJob.totalItemCount.toLocaleString()} items`
                            : undefined
                    }
                />
            ) : selectedOptimistic?.status === 'uploading' ? (
                <ImportProcessingCard title="Uploading your import…" />
            ) : selectedOptimistic?.status === 'failed' ? (
                <Alert status="error">
                    <AlertIcon />
                    {selectedOptimistic.errorMessage || 'Import upload failed.'}
                </Alert>
            ) : selectedJob?.status === 'failed' ? (
                <Alert status="error">
                    <AlertIcon />
                    {selectedJob.errorMessage || 'Import job failed.'}
                </Alert>
            ) : null}

            {imageCrop ? (
                <InventoryImageCropDialog
                    isOpen={imageCropDisclosure.isOpen}
                    imageSrc={imageCrop.url}
                    fileName={`${imageCrop.rowId}.jpg`}
                    fileType="image/jpeg"
                    title={`${imageCrop.name.trim() || '—'} · ${imageCrop.mfg.trim() || '—'}`}
                    onCancel={() => {
                        imageCropDisclosure.onClose();
                        setImageCrop(null);
                    }}
                    onConfirm={(file) => {
                        void handleImageCropConfirm(file);
                    }}
                />
            ) : null}
        </Box>
    );
}

function PreviewFieldCell({
    fieldId,
    displayRow,
    serverRow,
    editable,
    dirtyFields,
    autoFocus,
    onDirty,
    onFocusAdvance,
    advanceOnSelect = false,
}: {
    fieldId: string;
    displayRow: InventoryImportDraftRow;
    serverRow: InventoryImportDraftRow;
    editable: boolean;
    dirtyFields?: Set<string>;
    autoFocus: boolean;
    onDirty: (changes: InventoryImportDraftRowChanges) => void;
    onFocusAdvance: (opts: {
        exit: boolean;
        fromFieldId?: string;
        direction?: 'forward' | 'backward';
    }) => void;
    /** When true (issue Edit chain), selecting from a dropdown/typeahead advances. */
    advanceOnSelect?: boolean;
}) {
    const stringValue = (() => {
        const v = displayRow[fieldId as keyof InventoryImportDraftRow];
        if (v == null) return '';
        if (Array.isArray(v)) return v.join('; ');
        return String(v);
    })();

    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(stringValue);
    const skipBlurCommitRef = useRef(false);
    const displayWrapRef = useRef<HTMLDivElement>(null);
    // Column width captured while in display mode; edit mode is pinned to it so
    // swapping in editors never resizes the table columns.
    const lockedWidthRef = useRef<number | null>(null);
    const issueMsg = issueMessageForField(serverRow, fieldId, dirtyFields);
    const field = FIELD_BY_ID[fieldId];
    const isEditing = editable && (editing || autoFocus);
    const isNumericField =
        fieldId === 'reorderQuantity' ||
        fieldId === 'targetQuantity' ||
        fieldId === 'initialInStockQuantity' ||
        fieldId === 'unitCost';

    useEffect(() => {
        if (isEditing) {
            setDraft(stringValue);
            skipBlurCommitRef.current = false;
        }
    }, [isEditing, stringValue]);

    // No dep array: re-measure after every display-mode render so the latest
    // pre-edit column width is available when edit mode mounts.
    useLayoutEffect(() => {
        if (isEditing) return;
        const td = displayWrapRef.current?.closest('td');
        if (!td) return;
        const cs = window.getComputedStyle(td);
        lockedWidthRef.current =
            td.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
    });

    const resolveCommitValue = (container: HTMLElement | null): string => {
        const isLiveInputField =
            fieldId === 'initialInStockQuantity' ||
            fieldId === 'reorderQuantity' ||
            fieldId === 'targetQuantity' ||
            fieldId === 'unitCost';
        if (!isLiveInputField || !container) return draft;
        const input = container.querySelector<HTMLInputElement>('input');
        if (!input) return draft;
        const raw = input.value.trim().replace(/[,$]/g, '');
        if (
            (fieldId === 'reorderQuantity' || fieldId === 'targetQuantity') &&
            raw === ''
        ) {
            return '';
        }
        return raw === '' ? '0' : raw;
    };

    const commitString = (value: string) => {
        if (fieldId === 'reorderQuantity' || fieldId === 'targetQuantity') {
            const trimmed = value.trim();
            onDirty({
                [fieldId]: trimmed === '' ? null : Number(trimmed) || 0,
            } as InventoryImportDraftRowChanges);
        } else if (fieldId === 'initialInStockQuantity') {
            onDirty({ [fieldId]: Number(value) || 0 });
        } else if (fieldId === 'unitCost') {
            onDirty({ unitCost: Number(value) || 0 });
        } else if (fieldId === 'restockAlert') {
            const v = value.trim().toLowerCase();
            onDirty({
                restockAlert: ['true', 'yes', 'y', '1'].includes(v),
            });
        } else if (fieldId === 'origin') {
            onDirty({ origin: value.trim() || null });
        } else if (fieldId === 'category') {
            onDirty({ category: value as InventoryCategory });
        } else if (fieldId === 'partType') {
            onDirty({ partType: parseInventoryPartType(value) });
        } else if (fieldId === 'unit') {
            onDirty({ unit: value as InventoryUnit });
        } else if (fieldId === 'manufacturerPartNumber') {
            onDirty({ manufacturerPartNumber: formatPartNumber(value) });
        } else if (fieldId === 'additionalPartNumbers') {
            const parts = value
                .split(/[;,]/)
                .map((t) => formatPartNumber(t))
                .filter(Boolean);
            onDirty({ additionalPartNumbers: parts });
        } else if (fieldId === 'tagNames' || fieldId === 'binNames') {
            // handled separately
        } else {
            onDirty({ [fieldId]: value } as InventoryImportDraftRowChanges);
        }
    };

    const displayText =
        fieldId === 'unitCost'
            ? displayRow.unitCost != null
                ? formatPrice(displayRow.unitCost)
                : '—'
              : fieldId === 'category'
              ? categoryDisplayLabel(displayRow.category) || '—'
              : fieldId === 'unit' && stringValue
                ? formatInventoryUnitLabel(stringValue)
                : fieldId === 'partType' && stringValue
                  ? formatInventoryPartTypeLabel(stringValue)
                  : stringValue || '—';

    if (fieldId === 'restockAlert') {
        const isOn = displayRow.restockAlert === true;
        return (
            <HStack align="center" spacing={1}>
                <Switch
                    isChecked={isOn}
                    isDisabled={!editable}
                    onChange={(e) => {
                        if (!editable) return;
                        onDirty({ restockAlert: e.target.checked });
                    }}
                />
                <FieldIssueIcon message={issueMsg} />
            </HStack>
        );
    }

    if (fieldId === 'binNames') {
        const seen = new Set<string>();
        const bins: InventoryBin[] = [];
        (displayRow.binNames ?? []).forEach((name) => {
            const key = name.trim().toLowerCase();
            if (!key || seen.has(key)) return;
            seen.add(key);
            bins.push({
                _id: `draft:${name}`,
                name,
                locationId: displayRow.locationId ?? '',
            });
        });
        return (
            <HStack align="center" spacing={1} minW="160px">
                {editable ? (
                    <Box flex={1}>
                        <InventoryBinsField
                            value={bins}
                            label=""
                            locationId={displayRow.locationId}
                            allowCreate
                            onChange={(next) => {
                                const deduped: string[] = [];
                                const seenNames = new Set<string>();
                                next.forEach((b) => {
                                    const key = b.name.trim().toLowerCase();
                                    if (!key || seenNames.has(key)) return;
                                    seenNames.add(key);
                                    deduped.push(b.name.trim());
                                });
                                onDirty({ binNames: deduped });
                                onFocusAdvance({ exit: true });
                            }}
                        />
                    </Box>
                ) : (
                    <Text fontSize="sm">{(displayRow.binNames ?? []).join(', ') || '—'}</Text>
                )}
                <FieldIssueIcon message={issueMsg} />
            </HStack>
        );
    }

    if (fieldId === 'tagNames') {
        const tags: InventoryTag[] = dedupeTagNames(displayRow.tagNames ?? []).map((name) =>
            draftInventoryTag(name),
        );
        return (
            <HStack align="center" spacing={1} minW="160px">
                {editable ? (
                    <Box flex={1}>
                        <InventoryTagsField
                            value={tags}
                            label=""
                            onChange={(next) => {
                                onDirty({
                                    tagNames: dedupeTagNames(next.map((t) => t.name)),
                                });
                                onFocusAdvance({ exit: true });
                            }}
                        />
                    </Box>
                ) : (
                    <Text fontSize="sm">{(displayRow.tagNames ?? []).join(', ') || '—'}</Text>
                )}
                <FieldIssueIcon message={issueMsg} />
            </HStack>
        );
    }

    const startEdit = () => {
        if (!editable) return;
        setDraft(stringValue);
        setEditing(true);
    };

    if (!field) {
        return <Text fontSize="sm">—</Text>;
    }

    const displayMaxW = isNumericField
        ? undefined
        : fieldId === 'supplierName'
          ? '317px'
          : fieldId === 'locationTitle'
            ? '264px'
            : '220px';

    if (!isEditing) {
        return (
            <HStack
                ref={displayWrapRef}
                align="center"
                spacing={1}
                display="inline-flex"
                maxW={displayMaxW}
            >
                {editable ? (
                    <Tooltip label="Click to edit field" hasArrow>
                        <Text
                            fontSize="sm"
                            cursor="pointer"
                            onClick={startEdit}
                            _hover={{ bg: 'blackAlpha.50' }}
                            px={1}
                            borderRadius="sm"
                            noOfLines={isNumericField ? 1 : 2}
                        >
                            {displayText}
                        </Text>
                    </Tooltip>
                ) : (
                    <Text fontSize="sm">{displayText}</Text>
                )}
                <FieldIssueIcon
                    message={issueMsg}
                    onClick={editable ? startEdit : undefined}
                />
            </HStack>
        );
    }

    const lockedWidth = lockedWidthRef.current;

    return (
        <HStack
            align="center"
            spacing={1}
            display="inline-flex"
            w={lockedWidth != null ? `${lockedWidth}px` : undefined}
            maxW={lockedWidth != null ? `${lockedWidth}px` : undefined}
        >
            <Box
                flex={1}
                minW={0}
                onKeyDownCapture={(e) => {
                    if (e.key === 'Enter' || e.key === 'Tab') {
                        e.preventDefault();
                        const direction =
                            e.key === 'Tab' && e.shiftKey ? 'backward' : 'forward';
                        // Prevent the blur (from unmounting this editor) from
                        // committing again / clearing the chain we just set.
                        skipBlurCommitRef.current = true;
                        commitString(resolveCommitValue(e.currentTarget));
                        setEditing(false);
                        onFocusAdvance({ exit: false, fromFieldId: fieldId, direction });
                    }
                    if (e.key === 'Escape') {
                        setDraft(stringValue);
                        setEditing(false);
                        onFocusAdvance({ exit: true });
                    }
                }}
                onBlurCapture={(e) => {
                    if (skipBlurCommitRef.current) {
                        skipBlurCommitRef.current = false;
                        return;
                    }
                    const next = e.relatedTarget as HTMLElement | null;
                    if (next && e.currentTarget.contains(next)) return;
                    // Focus moving into a portaled dropdown (inline select menu)
                    // is not a DOM descendant but is still part of this editor.
                    if (next?.closest?.('[role="menu"]')) return;
                    commitString(resolveCommitValue(e.currentTarget));
                    setEditing(false);
                    onFocusAdvance({ exit: true });
                }}
            >
                <AutoFocusWrap active={isEditing}>
                    <InventoryImportDefaultValueField
                        field={field}
                        value={draft}
                        hideLabel
                        onChange={(val) => setDraft(val)}
                        onCommit={(val) => {
                            skipBlurCommitRef.current = true;
                            setDraft(val);
                            commitString(val);
                            setEditing(false);
                            // Discrete picks only walk the issue-resolution chain.
                            // Outside that (click-to-edit / Tab-nav), commit and exit.
                            if (advanceOnSelect) {
                                onFocusAdvance({ exit: false, fromFieldId: fieldId });
                            } else {
                                onFocusAdvance({ exit: true });
                            }
                        }}
                        onDismiss={() => {
                            setEditing(false);
                            onFocusAdvance({ exit: true });
                        }}
                    />
                </AutoFocusWrap>
            </Box>
            <FieldIssueIcon message={issueMsg} />
        </HStack>
    );
}

function AutoFocusWrap({ active, children }: { active: boolean; children: React.ReactNode }) {
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!active || !ref.current) return;
        const el = ref.current.querySelector<HTMLElement>(
            'input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        el?.focus();
    }, [active]);
    return <Box ref={ref}>{children}</Box>;
}

function PreviewRow({
    row,
    displayRow,
    editable,
    skipped,
    dirtyFields,
    focusField,
    issueFocusActive = false,
    onToggleSkip,
    onEdit,
    onDirty,
    onFocusAdvance,
    onViewImage,
}: {
    row: InventoryImportDraftRow;
    displayRow: InventoryImportDraftRow;
    editable: boolean;
    skipped: boolean;
    dirtyFields?: Set<string>;
    focusField: string | null;
    /** True while this row is in the issue-resolution Edit focus chain. */
    issueFocusActive?: boolean;
    onToggleSkip: () => void;
    onEdit: () => void;
    onDirty: (changes: InventoryImportDraftRowChanges) => void;
    onFocusAdvance: (opts: {
        exit: boolean;
        fromFieldId?: string;
        direction?: 'forward' | 'backward';
    }) => void;
    onViewImage: (url: string, name: string, mfg: string) => void;
}) {
    const showAsFlagged = rowHasUndirtyIssueField(row, dirtyFields);
    const imageIssue = issueMessageForField(row, 'imageUrl', dirtyFields);

    return (
        <Tr bg={showAsFlagged ? 'orange.50' : undefined}>
            <Td>
                {showAsFlagged ? (
                    <VStack align="stretch" spacing={1}>
                        <Button
                            size="xs"
                            variant={skipped ? 'solid' : 'outline'}
                            bg={skipped ? 'black' : 'transparent'}
                            color={skipped ? 'white' : 'black'}
                            borderColor="black"
                            borderWidth="1px"
                            _hover={{
                                bg: skipped ? 'gray.800' : 'blackAlpha.50',
                            }}
                            onClick={onToggleSkip}
                        >
                            Skip
                        </Button>
                        <Button size="xs" variant="outline" colorScheme="blue" onClick={onEdit}>
                            Edit
                        </Button>
                    </VStack>
                ) : (
                    <Tooltip label="No issues to resolve!" hasArrow>
                        <Box as="span" color="green.500" display="inline-flex">
                            <Icon as={TbCheck as React.ElementType} boxSize={4} />
                        </Box>
                    </Tooltip>
                )}
            </Td>
            {(
                [
                    'name',
                    'manufacturerPartNumber',
                    'additionalPartNumbers',
                    'category',
                    'partType',
                    'supplierName',
                    'initialInStockQuantity',
                    'unitCost',
                    'locationTitle',
                    'unit',
                    'restockAlert',
                    'origin',
                    'reorderQuantity',
                    'targetQuantity',
                    'binNames',
                    'tagNames',
                ] as const
            ).map((fieldId) => (
                <Td
                    key={fieldId}
                    isNumeric={
                        fieldId === 'initialInStockQuantity' ||
                        fieldId === 'unitCost' ||
                        fieldId === 'reorderQuantity' ||
                        fieldId === 'targetQuantity'
                    }
                    minW={
                        fieldId === 'supplierName'
                            ? '202px'
                            : fieldId === 'locationTitle'
                              ? '168px'
                              : fieldId === 'unit'
                                ? '88px'
                                : undefined
                    }
                >
                    <PreviewFieldCell
                        fieldId={fieldId}
                        displayRow={displayRow}
                        serverRow={row}
                        editable={editable}
                        dirtyFields={dirtyFields}
                        autoFocus={focusField === fieldId}
                        advanceOnSelect={issueFocusActive && focusField === fieldId}
                        onDirty={onDirty}
                        onFocusAdvance={onFocusAdvance}
                    />
                </Td>
            ))}
            <Td>
                <HStack align="center" spacing={1}>
                    {displayRow.imageUrl ? (
                        <IconButton
                            aria-label="View image"
                            icon={<Icon as={TbEye as React.ElementType} />}
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                                onViewImage(
                                    displayRow.imageUrl!,
                                    displayRow.name,
                                    displayRow.manufacturerPartNumber,
                                )
                            }
                        />
                    ) : (
                        <Text color="gray.400">—</Text>
                    )}
                    <FieldIssueIcon message={imageIssue} />
                </HStack>
            </Td>
        </Tr>
    );
}
